import React, { Component } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import { getOutlet, getOutletDayBookings, setOutletBookingStatus } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Table, Chip, StatRow, MiniStat, Meter, ConfirmDialog } from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";
import { OCCASIONS } from "./outletMeta";

/**
 * An outlet's day at the host stand: every reservation by time, and how full
 * each slot is against the outlet's covers limit.
 *
 * GET /api/outlets/:id/bookings?date= does the day boundaries in the branch's
 * timezone and returns that zone, so times here are shown on the outlet's
 * clock rather than the browser's. The first load sends no date and takes
 * "today" from the server for the same reason.
 *
 * The host moves each booking along: seated, then completed, or a no-show /
 * cancellation, which frees its covers. Any of those can be put back to
 * confirmed if pressed by mistake.
 */

/** YYYY-MM-DD shifted by whole days — calendar arithmetic, no timezone involved. */
const shiftDay = (ymd, days) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

const longDay = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });
};

const STATUS = {
  confirmed: { label: "Booked", tone: "good" },
  seated: { label: "Seated", tone: "warn" },
  completed: { label: "Completed", tone: "neutral" },
  no_show: { label: "No-show", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "bad" },
};

/** Rows from before booking statuses only carry `cancelled`. */
const statusOf = (b) => (b.status && STATUS[b.status] ? b.status : b.cancelled ? "cancelled" : "confirmed");
const freesCovers = (st) => st === "cancelled" || st === "no_show";

const guestName = (b) => {
  const u = b.user || {};
  return [u.firstName, u.lastName].filter(Boolean).join(" ") || "Guest";
};

class OutletDay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      outletId: props.match.params.id,
      outlet: null,
      date: null, // set from the server's first answer
      today: null,
      timezone: null,
      bookings: [],
      summary: null,
      isLoaded: false,
      updatingId: null,
      cancelling: null, // booking awaiting the cancel confirmation
    };
  }

  componentDidMount() {
    getOutlet(this.state.outletId).then((res) => {
      if (res && res.success) this.setState({ outlet: res.data });
      else {
        toast.error((res && res.message) || "Outlet not found");
        this.props.history.push("/outlets");
      }
    });
    this.loadDay(null);
  }

  loadDay = (date) => {
    this.setState({ isLoaded: false });
    getOutletDayBookings(this.state.outletId, date).then((res) => {
      if (res && res.success) {
        this.setState((s) => ({
          isLoaded: true,
          bookings: res.data || [],
          summary: res.summary || null,
          date: res.date,
          timezone: res.timezone,
          today: s.today || (date ? null : res.date),
        }));
      } else {
        this.setState({ isLoaded: true });
        toast.error((res && res.message) || "Could not load the bookings");
      }
    });
  };

  setStatus = (booking, status) => {
    this.setState({ updatingId: booking._id });
    setOutletBookingStatus(booking._id, status).then((res) => {
      this.setState({ updatingId: null, cancelling: null });
      if (res && res.success) {
        toast.success(`${guestName(booking)} · ${STATUS[status].label.toLowerCase()}`);
        this.loadDay(this.state.date); // the summary and slot covers change with it
      } else {
        toast.error((res && res.message) || "Could not update the booking");
      }
    });
  };

  renderActions(b) {
    const st = statusOf(b);
    const busy = this.state.updatingId === b._id;
    const btn = (label, status, kind = "quiet") => (
      <Btn key={status} kind={kind} disabled={busy} style={{ height: 32, padding: "0 12px", fontSize: 13 }}
        onClick={() => (status === "cancelled" ? this.setState({ cancelling: b }) : this.setStatus(b, status))}>
        {label}
      </Btn>
    );
    if (st === "confirmed") return [btn("Seat", "seated", "primary"), btn("No-show", "no_show"), btn("Cancel", "cancelled", "ghost")];
    if (st === "seated") return [btn("Done", "completed", "primary"), btn("Undo", "confirmed", "ghost")];
    return [btn("Reopen", "confirmed", "ghost")];
  }

  timeOf = (iso) => {
    if (!iso) return "—";
    try {
      // 24-hour, as the slots are named ("19:30"); h23 so midnight never reads "24:00".
      return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: this.state.timezone || undefined });
    } catch (e) {
      return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    }
  };

  columns(write) {
    const dim = (b) => freesCovers(statusOf(b));
    return [
      { key: "time", label: "Time", width: 80, render: (b) => <span style={{ fontSize: 15, fontWeight: 500, color: dim(b) ? C.taupeLight : C.ink }}>{this.timeOf(b.slotTime)}</span> },
      {
        key: "guest",
        label: "Guest",
        render: (b) => (
          <div style={{ opacity: dim(b) ? 0.55 : 1 }}>
            <div style={{ fontSize: 14, color: C.ink }}>{guestName(b)}</div>
            {b.user && b.user.mobile && <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>{b.user.mobile}</div>}
          </div>
        ),
      },
      { key: "seats", label: "Party", width: 70, render: (b) => <span style={{ color: C.ink }}>{b.seats || "—"}</span> },
      { key: "area", label: "Area", render: (b) => <span style={{ fontSize: 13, color: C.slate }}>{b.seatingArea || "Any"}</span> },
      {
        key: "notes",
        label: "Notes",
        render: (b) => (
          <div style={{ fontSize: 13, color: C.slate, maxWidth: 280 }}>
            {b.occasion && OCCASIONS[b.occasion] && <Chip tone="warn">{OCCASIONS[b.occasion]}</Chip>}
            {b.specialRequests && <div style={{ marginTop: b.occasion && OCCASIONS[b.occasion] ? 5 : 0 }}>{b.specialRequests}</div>}
            {!b.specialRequests && !(b.occasion && OCCASIONS[b.occasion]) && <span style={{ color: C.taupeLight }}>—</span>}
          </div>
        ),
      },
      { key: "ref", label: "Ref", render: (b) => <span style={{ fontSize: 13, color: C.taupe }}>{b.bookingNumber || `#${String(b._id).slice(-6).toUpperCase()}`}</span> },
      { key: "status", label: "Status", width: 100, render: (b) => { const st = STATUS[statusOf(b)]; return <Chip tone={st.tone}>{st.label}</Chip>; } },
      ...(write
        ? [{ key: "actions", label: "", render: (b) => <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>{this.renderActions(b)}</div> }]
        : []),
    ];
  }

  render() {
    const { outlet, date, today, bookings, summary, isLoaded, timezone, cancelling, updatingId } = this.state;
    const write = canWrite();
    const s = summary || { bookings: 0, covers: 0, cancelled: 0, maxCoversPerSlot: null, slots: [] };
    const busiest = (s.slots || []).reduce((best, x) => (!best || x.covers > best.covers ? x : best), null);
    const limit = s.maxCoversPerSlot;

    return (
      <ConsolePage
        title={outlet ? `${outlet.name} · bookings` : "Bookings"}
        subtitle={date ? `${longDay(date)}${timezone ? ` · ${timezone.replace("_", " ")} time` : ""}` : "Loading…"}
        actions={
          <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/outlets")}>
            Back to outlets
          </Btn>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Btn kind="quiet" icon="chevron_left" disabled={!date} onClick={() => this.loadDay(shiftDay(date, -1))}>Previous</Btn>
            <div style={{ width: 180 }}>
              <Input
                type="date"
                aria-label="Day"
                value={date || ""}
                onChange={(e) => e.target.value && this.loadDay(e.target.value)}
                style={{ height: 42, borderRadius: 12, border: `1px solid ${C.border}` }}
              />
            </div>
            <Btn kind="quiet" icon="chevron_right" disabled={!date} onClick={() => this.loadDay(shiftDay(date, 1))}>Next</Btn>
            {today && date !== today && <Btn kind="ghost" onClick={() => this.loadDay(today)}>Today</Btn>}
          </div>

          <StatRow>
            <MiniStat label="Bookings" value={s.bookings} hint="not cancelled" />
            <MiniStat label="Covers" value={s.covers} hint="guests expected" />
            <MiniStat label="Cancelled" value={s.cancelled} />
            <MiniStat
              label="Busiest slot"
              value={busiest ? busiest.time : "—"}
              hint={busiest ? `${busiest.covers} covers${limit ? ` of ${limit}` : ""}` : "no bookings"}
              tone={busiest && limit && busiest.covers >= limit ? "warn" : undefined}
            />
          </StatRow>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 18, alignItems: "start" }}>
            <Panel title="Reservations" subtitle={outlet && !outlet.acceptsReservations ? "This outlet isn't taking new reservations." : null}>
              <div style={{ marginTop: 14 }}>
                {isLoaded ? (
                  <Table columns={this.columns(write)} rows={bookings} empty="No reservations for this day" />
                ) : (
                  <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>Loading…</div>
                )}
              </div>
            </Panel>

            <Panel title="By slot" subtitle={limit ? `Limit ${limit} covers a slot` : "No covers limit set"}>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                {(s.slots || []).length ? (
                  s.slots.map((x) => (
                    <Meter
                      key={x.time}
                      label={`${x.time} · ${x.bookings} booking${x.bookings === 1 ? "" : "s"}`}
                      value={x.covers}
                      max={limit || Math.max(...s.slots.map((y) => y.covers), 1)}
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: C.taupeLight }}>Nothing booked yet.</div>
                )}
              </div>
            </Panel>
          </div>
        </div>
        <ConfirmDialog
          isOpen={!!cancelling}
          toggle={() => !updatingId && this.setState({ cancelling: null })}
          loading={!!updatingId}
          onConfirm={() => this.setStatus(cancelling, "cancelled")}
          title={`Cancel ${cancelling ? guestName(cancelling) : "this"}'s booking?`}
          body="Their covers go back on sale for that time. You can reopen it if this was a mistake."
          confirmText="Cancel booking"
          cancelText="Keep it"
        />
      </ConsolePage>
    );
  }
}

export default OutletDay;
